import React, {	useRef,
	useEffect,
	useState,
	forwardRef,
	useImperativeHandle,
} from "react";
import {FaceMesh} from "@mediapipe/face_mesh";
import {Camera} from "@mediapipe/camera_utils";
import * as THREE from "three";
import {GLTFLoader} from "three/addons/loaders/GLTFLoader.js";
import "./AppCanvas.css";

const AppCanvas = forwardRef(function AppCanvas(
	{glassesImageUrl, model3DUrl},
	ref
) {
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const overlayCanvasRef = useRef(null);

	// 2D Model State
	const [loading, setLoading] = useState(true);
	const [glasses, setGlasses] = useState(new window.Image());
	const [glassesLoaded, setGlassesLoaded] = useState(false);
	const [faceDetected, setFaceDetected] = useState(false);
	const [mediaPipeError, setMediaPipeError] = useState(false);

	// 3D Model State
	const [model3DLoaded, setModel3DLoaded] = useState(false);
	const [loading3D, setLoading3D] = useState(false);
	const [modelType, setModelType] = useState("2D"); // '2D', '3D', or 'both'
	const [activeModel, setActiveModel] = useState("2D"); // '2D' or '3D'
	const [forceUpdate, setForceUpdate] = useState(0); // Force re-render counter

	// 3D Scene Refs
	const sceneRef = useRef(null);
	const rendererRef = useRef(null);
	const cameraRef = useRef(null);
	const glasses3DRef = useRef(null);
	const faceMeshRef = useRef(null);
	const cameraInstanceRef = useRef(null);

	// Enhanced tracking state with face dimensions
	const lastPosition = useRef({
		x: 0,
		y: 0,
		z: 0,
		scale: {x: 1, y: 1, z: 1},
		rotation: {x: 0, y: 0, z: 0},
		faceWidth: 0,
		faceHeight: 0,
	});

	// FIXED: Use refs to track current state for immediate access
	const currentActiveModelRef = useRef("2D");
	const currentModel3DLoadedRef = useRef(false);

	// Smoothing factors
	const smoothingFactors = {
		position: 0.7,
		rotation: 0.5,
		scale: 0.05,
		dimensions: 0.1,
	};

	// Force 3D mode activation - Direct approach
	const forceActivate3DMode = () => {
		console.log("=== FORCE ACTIVATING 3D MODE ===");

		// FIXED: Update both state and ref immediately
		setActiveModel("3D");
		currentActiveModelRef.current = "3D";

		// Force re-render
		setForceUpdate(prev => prev + 1);

		// Ensure 3D scene is ready
		if (!sceneRef.current) {
			console.log("Initializing 3D scene for force activation...");
			initializeThreeJS();
		}

		// Start render loop
		startRenderLoop();

		// Force 3D model to be visible
		if (glasses3DRef.current) {
			glasses3DRef.current.visible = true;
			glasses3DRef.current.position.set(0, 0, 0);
			glasses3DRef.current.scale.set(0.01, 0.01, 0.005);
			console.log("3D model force activated");
		}

		console.log("=== 3D MODE FORCE ACTIVATED ===");
	};

	// ENHANCED: Model type detection with automatic switching
	const detectModelType = (imageUrl, model3DUrl) => {
		console.log("Detecting model type:", {imageUrl, model3DUrl});

		if (imageUrl && model3DUrl) {
			console.log("Both 2D and 3D models available - enabling both");
			return "both";
		} else if (model3DUrl) {
			console.log("Only 3D model available - enabling 3D mode");
			return "3D";
		} else if (imageUrl) {
			console.log("Only 2D model available - enabling 2D mode");
			return "2D";
		}

		console.log("No models available - defaulting to 2D");
		return "2D";
	};

	// Initialize 3D scene - Enhanced from SimpleVTO.jsx
	const initializeThreeJS = () => {
		if (!overlayCanvasRef.current) {
			console.warn("Overlay canvas not available for 3D scene");
			return;
		}

		console.log("Initializing optimized 3D scene...");

		sceneRef.current = new THREE.Scene();
		sceneRef.current.background = null;

		rendererRef.current = new THREE.WebGLRenderer({
			canvas: overlayCanvasRef.current,
			alpha: true,
			antialias: true,
			preserveDrawingBuffer: true,
		});
		rendererRef.current.setSize(640, 480);
		rendererRef.current.setClearColor(0x000000, 0);

		cameraRef.current = new THREE.PerspectiveCamera(
			60,
			640 / 480,
			0.1,
			500
		);
		cameraRef.current.position.set(0, 0, 0.6);

		// Simple lighting - Enhanced from SimpleVTO.jsx
		const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
		sceneRef.current.add(ambientLight);

		const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
		directionalLight.position.set(1, 2, 3);
		sceneRef.current.add(directionalLight);

		console.log("3D scene ready");
	};

	// Load 3D GLTF model - Enhanced from SimpleVTO.jsx
	const load3DModel = async modelUrl => {
		if (!modelUrl) {
			console.warn("No 3D model URL provided");
			return;
		}

		setLoading3D(true);
		console.log("Loading 3D glasses model from:", modelUrl);

		try {
			// Ensure scene is initialized
			if (!sceneRef.current) {
				console.log("Initializing 3D scene for model loading...");
				initializeThreeJS();
			}

			const loader = new GLTFLoader();
			const gltf = await new Promise((resolve, reject) => {
				loader.load(
					modelUrl,
					gltf => {
						console.log("3D GLTF model loaded successfully");
						resolve(gltf);
					},
					progress => {
						const percent =
							(progress.loaded / progress.total) * 100;
						if (percent % 25 === 0) {
							console.log(`Loading: ${percent.toFixed(0)}%`);
						}
					},
					error => {
						console.error(`Model error: ${error.message}`);
						reject(error);
					}
				);
			});

			// Clear previous model only if scene exists
			if (glasses3DRef.current && sceneRef.current) {
				sceneRef.current.remove(glasses3DRef.current);
			}

			// Extract the scene from GLTF
			glasses3DRef.current = gltf.scene;
			glasses3DRef.current.position.set(0, 0, 0);
			glasses3DRef.current.scale.set(0.01, 0.01, 0.005); // Much smaller initial scale from SimpleVTO.jsx
			glasses3DRef.current.visible = false; // Start hidden until face is detected

			// Materials - Enhanced from SimpleVTO.jsx
			glasses3DRef.current.traverse(child => {
				if (child.isMesh) {
					child.material = new THREE.MeshBasicMaterial({
						color: 0x2a2a2a,
						transparent: true,
						opacity: 0.95,
						side: THREE.DoubleSide,
						depthWrite: false,
						depthTest: false,
					});
				}
			});

			// Only add to scene if it exists
			if (sceneRef.current) {
				sceneRef.current.add(glasses3DRef.current);

				// FIXED: Update both state and ref synchronously
				currentModel3DLoadedRef.current = true;
				setModel3DLoaded(true);

				console.log("3D glasses ready for tracking - State updated:", {
					model3DLoaded: true,
					refValue: currentModel3DLoadedRef.current,
					glasses3DRef: !!glasses3DRef.current,
				});
			} else {
				console.error("Scene not initialized, cannot add 3D model");
				throw new Error("3D scene not initialized");
			}
		} catch (error) {
			console.error(`Failed to load 3D model: ${error.message}`);
			setModel3DLoaded(false);
		} finally {
			setLoading3D(false);
		}
	};

	// IMPROVED: Enhanced face dimension calculation from SimpleVTO.jsx
	const calculateFaceDimensions = landmarks => {
		try {
			// More accurate face width using multiple reference points
			const leftCheek = landmarks[234]; // Left cheek
			const rightCheek = landmarks[454]; // Right cheek
			const leftTemple = landmarks[127]; // Left temple
			const rightTemple = landmarks[356]; // Right temple

			// More accurate face height using forehead to chin
			const foreheadTop = landmarks[10]; // Top of forehead
			const chinBottom = landmarks[152]; // Bottom of chin
			const noseTip = landmarks[1]; // Nose tip for reference

			let faceWidth = 0;
			let faceHeight = 0;

			// Calculate face width using multiple methods and take average
			const widthMethods = [];

			// Method 1: Cheek to cheek
			if (leftCheek && rightCheek) {
				const cheekWidth = Math.sqrt(
					Math.pow(rightCheek.x - leftCheek.x, 2) +
						Math.pow(rightCheek.y - leftCheek.y, 2)
				);
				widthMethods.push(cheekWidth);
			}

			// Method 2: Temple to temple
			if (leftTemple && rightTemple) {
				const templeWidth = Math.sqrt(
					Math.pow(rightTemple.x - leftTemple.x, 2) +
						Math.pow(rightTemple.y - leftTemple.y, 2)
				);
				widthMethods.push(templeWidth);
			}

			// Use average of available width measurements
			if (widthMethods.length > 0) {
				faceWidth =
					widthMethods.reduce((a, b) => a + b, 0) /
					widthMethods.length;
			}

			// Calculate face height
			if (foreheadTop && chinBottom) {
				faceHeight = Math.sqrt(
					Math.pow(chinBottom.x - foreheadTop.x, 2) +
						Math.pow(chinBottom.y - foreheadTop.y, 2)
				);
			}

			// IMPROVED: Add face shape analysis
			const faceRatio = faceHeight > 0 ? faceWidth / faceHeight : 0;

			return {
				faceWidth,
				faceHeight,
				faceRatio,
				landmarkCount: Object.keys(landmarks).length,
			};
		} catch (error) {
			console.error("Face dimension calculation error:", error);
			return {
				faceWidth: 0,
				faceHeight: 0,
				faceRatio: 0,
				landmarkCount: 0,
			};
		}
	};

	// ENHANCED: Calculate glasses dimensions based on face size from SimpleVTO.jsx
	const calculateGlassesDimensions = (
		faceWidth,
		faceHeight,
		faceRatio,
		ipd
	) => {
		// SMALLER Base dimensions for more realistic glasses
		const BASE_FACE_WIDTH = 1; // Average face width in normalized coordinates
		const BASE_GLASSES_WIDTH = 0.3; // Much smaller base glasses width
		const BASE_GLASSES_HEIGHT = 0.15; // Much smaller base glasses height

		// Calculate width based on face width and IPD (reduced factors)
		let glassesWidth = 0;
		if (faceWidth > 0 && ipd > 0) {
			// Use both face width and IPD for better accuracy (much smaller factors)
			const widthFromFace =
				(faceWidth / BASE_FACE_WIDTH) * BASE_GLASSES_WIDTH;
			const widthFromIPD = ipd * 0.4; // Much smaller IPD-based width
			glassesWidth = (widthFromFace + widthFromIPD) / 2;
		} else if (faceWidth > 0) {
			glassesWidth = (faceWidth / BASE_FACE_WIDTH) * BASE_GLASSES_WIDTH;
		} else {
			glassesWidth = BASE_GLASSES_WIDTH;
		}

		// Calculate height based on face height and ratio (reduced factors)
		let glassesHeight = BASE_GLASSES_HEIGHT;
		if (faceHeight > 0) {
			// Adjust height based on face proportions (smaller adjustments)
			if (faceRatio < 0.7) {
				// Long face - slightly taller glasses
				glassesHeight = BASE_GLASSES_HEIGHT * 1.05;
			} else if (faceRatio > 0.9) {
				// Round face - slightly shorter glasses
				glassesHeight = BASE_GLASSES_HEIGHT * 0.95;
			} else {
				// Average face
				glassesHeight = BASE_GLASSES_HEIGHT;
			}
		}

		// Apply much smaller constraints for realistic glasses sizes
		const minWidth = 0.15;
		const maxWidth = 0.5;
		const minHeight = 0.1;
		const maxHeight = 0.3;

		return {
			width: Math.min(Math.max(glassesWidth, minWidth), maxWidth),
			height: Math.min(Math.max(glassesHeight, minHeight), maxHeight),
			depth: Math.min(Math.max(glassesWidth * 0.2, 0.05), 0.15),
		};
	};

	// Calculate 3D glasses position - Enhanced from SimpleVTO.jsx
	const calculate3DGlassesPosition = landmarks => {
		if (!landmarks) return null;

		try {
			// Key landmarks for glasses positioning - Enhanced from SimpleVTO.jsx
			const leftEyeOuter = landmarks[33];
			const rightEyeOuter = landmarks[263];
			const leftEyeInner = landmarks[133];
			const rightEyeInner = landmarks[362];
			const noseBridge = landmarks[168];

			if (
				!leftEyeOuter ||
				!rightEyeOuter ||
				!leftEyeInner ||
				!rightEyeInner ||
				!noseBridge
			) {
				return null;
			}

			// Calculate eye centers - Enhanced from SimpleVTO.jsx
			const leftEyeCenter = {
				x: (leftEyeOuter.x + leftEyeInner.x) / 2,
				y: (leftEyeOuter.y + leftEyeInner.y) / 2,
				z: (leftEyeOuter.z + leftEyeInner.z) / 2,
			};
			const rightEyeCenter = {
				x: (rightEyeOuter.x + rightEyeInner.x) / 2,
				y: (rightEyeOuter.y + rightEyeInner.y) / 2,
				z: (rightEyeOuter.z + rightEyeInner.z) / 2,
			};

			// Center between eyes - Enhanced from SimpleVTO.jsx
			const glassesCenter = {
				x: (leftEyeCenter.x + rightEyeCenter.x) / 2,
				y: (leftEyeCenter.y + rightEyeCenter.y) / 2,
				z: (leftEyeCenter.z + rightEyeCenter.z) / 2,
			};

			// ENHANCED: Calculate face dimensions with improved accuracy
			const faceDimensions = calculateFaceDimensions(landmarks);

			// Calculate IPD (Inter-Pupillary Distance) - Enhanced from SimpleVTO.jsx
			const ipd = Math.sqrt(
				Math.pow(rightEyeCenter.x - leftEyeCenter.x, 2) +
					Math.pow(rightEyeCenter.y - leftEyeCenter.y, 2)
			);

			// NEW: Calculate glasses dimensions based on face size - Enhanced from SimpleVTO.jsx
			const glassesDimensions = calculateGlassesDimensions(
				faceDimensions.faceWidth,
				faceDimensions.faceHeight,
				faceDimensions.faceRatio,
				ipd
			);

			// EXACT POINT POSITIONING: Position glasses exactly on drawn points - Enhanced from SimpleVTO.jsx
			// Use the exact same coordinates as the drawn center point
			const canvasWidth = 640;
			const canvasHeight = 480;

			// Convert the drawn center point coordinates to 3D world coordinates
			// This matches exactly where the red dot is drawn - IMPROVED from SimpleVTO.jsx
			const posX = (glassesCenter.x - 0.53) * 2; // Exact center point X
			const posY = -((glassesCenter.y - 0.55) * 2); // Exact center point Y (inverted)
			const posZ = (glassesCenter.z - 0.5) * 0.3; // Slightly closer to face

			// Rotation calculation - Enhanced from SimpleVTO.jsx
			const eyeLineAngle = -Math.atan2(
				rightEyeCenter.y - leftEyeCenter.y,
				rightEyeCenter.x - leftEyeCenter.x
			);

			return {
				x: posX,
				y: posY,
				z: posZ,
				scale: {
					x: glassesDimensions.width,
					y: glassesDimensions.height,
					z: glassesDimensions.depth,
				},
				rotation: {
					x: 0,
					y: 0,
					z: eyeLineAngle,
				},
				faceWidth: faceDimensions.faceWidth,
				faceHeight: faceDimensions.faceHeight,
			};
		} catch (error) {
			console.error("3D Position calculation error:", error);
			return null;
		}
	};

	// Smooth positioning for 3D - Enhanced from SimpleVTO.jsx
	const smooth3DPosition = (newPos, lastPos) => {
		if (!newPos) return lastPos;

		const lastScale =
			typeof lastPos.scale === "number"
				? {x: lastPos.scale, y: lastPos.scale, z: lastPos.scale}
				: lastPos.scale;

		const newScale =
			typeof newPos.scale === "number"
				? {x: newPos.scale, y: newPos.scale, z: newPos.scale}
				: newPos.scale;

		// Smooth face dimensions - Enhanced from SimpleVTO.jsx
		const smoothedFaceWidth =
			lastPos.faceWidth +
			(newPos.faceWidth - lastPos.faceWidth) *
				smoothingFactors.dimensions;
		const smoothedFaceHeight =
			lastPos.faceHeight +
			(newPos.faceHeight - lastPos.faceHeight) *
				smoothingFactors.dimensions;

		return {
			x: lastPos.x + (newPos.x - lastPos.x) * smoothingFactors.position,
			y: lastPos.y + (newPos.y - lastPos.y) * smoothingFactors.position,
			z: lastPos.z + (newPos.z - lastPos.z) * smoothingFactors.position,
			scale: {
				x:
					lastScale.x +
					(newScale.x - lastScale.x) * smoothingFactors.scale,
				y:
					lastScale.y +
					(newScale.y - lastScale.y) * smoothingFactors.scale,
				z:
					lastScale.z +
					(newScale.z - lastScale.z) * smoothingFactors.scale,
			},
			rotation: {
				x:
					lastPos.rotation.x +
					(newPos.rotation.x - lastPos.rotation.x) *
						smoothingFactors.rotation,
				y:
					lastPos.rotation.y +
					(newPos.rotation.y - lastPos.rotation.y) *
						smoothingFactors.rotation,
				z:
					lastPos.rotation.z +
					(newPos.rotation.z - lastPos.rotation.z) *
						smoothingFactors.rotation,
			},
			faceWidth: smoothedFaceWidth,
			faceHeight: smoothedFaceHeight,
		};
	};

	// Position 3D glasses on face - Enhanced from SimpleVTO.jsx
	const position3DGlassesOnFace = landmarks => {
		if (!glasses3DRef.current || !landmarks) {
			console.log("3D glasses or landmarks not available");
			return;
		}

		try {
			const newPosition = calculate3DGlassesPosition(landmarks);

			if (!newPosition) {
				console.log("No 3D position calculated, hiding glasses");
				glasses3DRef.current.visible = false;
				return;
			}

			// Apply smoothing - Enhanced from SimpleVTO.jsx
			const smoothedPosition = smooth3DPosition(
				newPosition,
				lastPosition.current
			);
			lastPosition.current = smoothedPosition;

			// Apply transformations - Enhanced from SimpleVTO.jsx
			glasses3DRef.current.position.set(
				smoothedPosition.x,
				smoothedPosition.y,
				smoothedPosition.z
			);

			const scale = smoothedPosition.scale;
			if (typeof scale === "object") {
				glasses3DRef.current.scale.set(scale.x, scale.y, scale.z);
			} else {
				glasses3DRef.current.scale.set(scale, scale, scale);
			}

			glasses3DRef.current.rotation.set(
				smoothedPosition.rotation.x,
				smoothedPosition.rotation.y,
				smoothedPosition.rotation.z
			);

			glasses3DRef.current.visible = true;
			console.log("3D glasses positioned and visible at:", {
				position: smoothedPosition,
				scale: scale,
				rotation: smoothedPosition.rotation,
			});
		} catch (error) {
			console.error("3D Tracking error:", error);
		}
	};

	// Render 3D overlay - Enhanced from SimpleVTO.jsx
	const render3DOverlay = () => {
		if (rendererRef.current && sceneRef.current && cameraRef.current) {
			rendererRef.current.render(sceneRef.current, cameraRef.current);
			console.log("3D overlay rendered");
		} else {
			console.log("3D render components not ready:", {
				renderer: !!rendererRef.current,
				scene: !!sceneRef.current,
				camera: !!cameraRef.current,
			});
		}
	};

	// Enhanced render loop from SimpleVTO.jsx
	const startRenderLoop = () => {
		console.log("Starting 3D render loop");
		const animate = () => {
			if (
				currentActiveModelRef.current === "3D" &&
				currentModel3DLoadedRef.current
			) {
				render3DOverlay();
			}
			requestAnimationFrame(animate);
		};
		animate();
	};

	// ENHANCED: Detect model type on mount with proper 3D detection
	useEffect(() => {
		console.log("Detecting model types:", {glassesImageUrl, model3DUrl});
		const detectedType = detectModelType(glassesImageUrl, model3DUrl);
		console.log("Detected model type:", detectedType);

		setModelType(detectedType);

		// FIXED: Properly set active model based on detection
		if (detectedType === "3D") {
			console.log("Setting active model to 3D");
			setActiveModel("3D");
		} else if (detectedType === "both") {
			console.log("Both models available, checking 3D model status");
			// If both models are available, prefer 3D if it's loaded, otherwise default to 2D
			if (model3DLoaded) {
				console.log(
					"3D model already loaded, setting active model to 3D"
				);
				setActiveModel("3D");
			} else {
				console.log("3D model not loaded yet, defaulting to 2D");
				setActiveModel("2D");
			}
		} else {
			console.log("Setting active model to 2D");
			setActiveModel("2D");
		}
	}, [glassesImageUrl, model3DUrl]);

	// FIXED: Auto-switch to 3D mode when 3D model becomes loaded
	useEffect(() => {
		if (model3DLoaded && modelType === "both" && activeModel === "2D") {
			console.log("3D model loaded, auto-switching from 2D to 3D mode");
			setActiveModel("3D");
			currentActiveModelRef.current = "3D";
		}
	}, [model3DLoaded, modelType, activeModel]);

	// Initialize 3D scene when 3D model is available
	useEffect(() => {
		if (modelType === "3D" || modelType === "both") {
			initializeThreeJS();
		}
	}, [modelType]);

	// ENHANCED: Load 3D model when URL is provided with proper enablement
	useEffect(() => {
		console.log("3D model loading effect:", {
			model3DUrl,
			modelType,
			activeModel,
		});

		if (model3DUrl && (modelType === "3D" || modelType === "both")) {
			console.log("Loading 3D model...");

			// Ensure 3D scene is initialized before loading model
			if (!sceneRef.current) {
				console.log("Initializing 3D scene before loading model...");
				initializeThreeJS();
			}

			load3DModel(model3DUrl)
				.then(() => {
					console.log("3D model loaded successfully");

					// FIXED: If only 3D model is available, ensure 3D mode is active
					if (modelType === "3D") {
						console.log(
							"Only 3D model available - ensuring 3D mode is active"
						);
						setActiveModel("3D");
						currentActiveModelRef.current = "3D";
					}

					// Start render loop after model is loaded
					if (activeModel === "3D" || modelType === "3D") {
						console.log("Starting 3D render loop");
						startRenderLoop();
					}
				})
				.catch(error => {
					console.error("Failed to load 3D model:", error);
					// If 3D model fails to load and it's the only model, fallback to 2D
					if (modelType === "3D") {
						console.log("3D model failed, falling back to 2D mode");
						setActiveModel("2D");
					}
				});
		}
	}, [model3DUrl, modelType, activeModel]);

	// ENHANCED: Handle model switching with integrated 3D logic
	useEffect(() => {
		console.log("=== MODEL SWITCHING EFFECT TRIGGERED ===", {
			activeModel,
			model3DLoaded,
			modelType,
		});

		if (activeModel === "3D") {
			console.log("Enabling 3D model with integrated detection logic", {
				model3DLoaded,
				glasses3DRef: !!glasses3DRef.current,
				sceneRef: !!sceneRef.current,
			});

			// Ensure 3D scene is ready
			if (!sceneRef.current) {
				console.log("Initializing 3D scene for model switching...");
				initializeThreeJS();
			}

			// Start render loop for 3D
			startRenderLoop();

			// Force 3D model to be visible and properly positioned
			if (glasses3DRef.current) {
				glasses3DRef.current.visible = true;
				glasses3DRef.current.position.set(0, 0, 0);
				glasses3DRef.current.scale.set(0.01, 0.01, 0.005);
				console.log("3D model enabled and positioned");
			} else {
				console.log("3D model not available for positioning");
			}

			console.log("3D model enabled with integrated detection");
		} else if (activeModel === "2D") {
			console.log("Enabling 2D model and disabling 3D");

			// Disable 3D model rendering
			if (glasses3DRef.current) {
				glasses3DRef.current.visible = false;
				console.log("3D model disabled for 2D mode");
			}

			console.log("2D model enabled");
		}
	}, [activeModel, model3DLoaded, modelType]);

	// Initialize with fallback image
	useEffect(() => {
		const fallbackGlasses = new window.Image();
		fallbackGlasses.src = glassesImageUrl;
		fallbackGlasses.onload = () => {
			setGlasses(fallbackGlasses);
			setGlassesLoaded(true);
		};
	}, [glassesImageUrl]);

	// Load custom glasses image when URL changes
	useEffect(() => {
		if (glassesImageUrl) {
			const customGlasses = new window.Image();
			customGlasses.crossOrigin = "anonymous";
			customGlasses.src = glassesImageUrl;
			customGlasses.onload = () => {
				console.log("Custom glasses loaded:", glassesImageUrl);
				setGlasses(customGlasses);
				setGlassesLoaded(true);
			};
			customGlasses.onerror = error => {
				console.error(
					"Failed to load custom glasses:",
					glassesImageUrl,
					error
				);
				// Fallback to default glasses
				const fallbackGlasses = new window.Image();
				fallbackGlasses.src = glassesImageUrl;
				fallbackGlasses.onload = () => {
					setGlasses(fallbackGlasses);
					setGlassesLoaded(true);
				};
			};
		}
	}, [glassesImageUrl]);

	// Expose methods to parent component
	useImperativeHandle(ref, () => ({
		setGlassesImage: imageUrl => {
			if (imageUrl) {
				const newGlasses = new window.Image();
				newGlasses.crossOrigin = "anonymous";
				newGlasses.src = imageUrl;
				newGlasses.onload = () => {
					console.log("Glasses image updated via ref:", imageUrl);
					setGlasses(newGlasses);
					setGlassesLoaded(true);
				};
				newGlasses.onerror = error => {
					console.error(
						"Failed to load glasses via ref:",
						imageUrl,
						error
					);
				};
			}
		},
		set3DModel: modelUrl => {
			if (modelUrl) {
				load3DModel(modelUrl);
			}
		},
		switchModel: modelType => {
			if (modelType === "2D" || modelType === "3D") {
				console.log(`Switching to ${modelType} model`);
				setActiveModel(modelType);

				// If switching to 3D and model is not loaded, try to load it
				if (modelType === "3D" && !model3DLoaded && model3DUrl) {
					console.log("Loading 3D model on switch...");
					load3DModel(model3DUrl);
				}
			}
		},
		getGlassesImage: () => glasses.src,
		getModelType: () => modelType,
		getActiveModel: () => activeModel,
	}));

	useEffect(() => {
		let camera = null;
		let faceMesh = null;
		let retryCount = 0;
		const maxRetries = 3;

		// Simplified MediaPipe initialization without preloading to avoid CORS issues
		const initializeMediaPipeSimple = () => {
			try {
				faceMesh = new FaceMesh({
					locateFile: file => {
						// Use jsdelivr CDN which is more reliable
						return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
					},
					onError: error => {
						console.warn("MediaPipe error:", error);
						setMediaPipeError(true);
					},
				});

				faceMesh.setOptions({
					maxNumFaces: 1,
					refineLandmarks: true,
					minDetectionConfidence: 0.5,
					minTrackingConfidence: 0.5,
				});

				faceMesh.onResults(onResults);

				faceMesh.onError = error => {
					console.error("MediaPipe initialization error:", error);
				};

				console.log("MediaPipe initialized successfully");
			} catch (error) {
				console.error("Failed to initialize MediaPipe:", error);
				fallbackMode();
			}
		};

		// Network connectivity check
		const checkNetworkConnectivity = () => {
			if (!navigator.onLine) {
				console.warn("No internet connection detected");
				setMediaPipeError(true);
				return false;
			}
			return true;
		};

		// Enhanced MediaPipe initialization with network check
		const initializeMediaPipeWithNetworkCheck = () => {
			if (!checkNetworkConnectivity()) {
				fallbackMode();
				return;
			}

			try {
				faceMesh = new FaceMesh({
					locateFile: file => {
						// Use a single reliable CDN to avoid conflicts
						return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
					},
					onError: error => {
						console.warn("MediaPipe network check error:", error);
						setMediaPipeError(true);
					},
				});

				faceMesh.setOptions({
					maxNumFaces: 1,
					refineLandmarks: true,
					minDetectionConfidence: 0.5,
					minTrackingConfidence: 0.5,
				});

				faceMesh.onResults(onResults);

				faceMesh.onError = error => {
					console.error(
						"MediaPipe network check initialization error:",
						error
					);
				};
			} catch (error) {
				console.error(
					"Failed to initialize MediaPipe with network check:",
					error
				);
				fallbackMode();
			}
		};

		const onResults = results => {
			const canvas = canvasRef.current;
			const ctx = canvas.getContext("2d");

			// DEBUG: Log active model state on every frame
			if (Math.random() < 0.01) {
				// Only log occasionally to avoid spam
				console.log(
					"onResults - Current activeModel:",
					activeModel,
					"Ref:",
					currentActiveModelRef.current,
					{
						glassesLoaded,
						model3DLoaded,
						model3DLoadedRef: currentModel3DLoadedRef.current,
						glasses3DRef: !!glasses3DRef.current,
					}
				);
			}

			// Clear canvas
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			// Draw video frame WITHOUT mirroring in canvas
			ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);

			// Handle face detection and model rendering
			if (
				results.multiFaceLandmarks &&
				results.multiFaceLandmarks.length > 0
			) {
				setFaceDetected(true);
				const landmarks = results.multiFaceLandmarks[0];

				// FIXED: Render models based on active model type with proper separation
				// Use ref for immediate state access to avoid async state issues
				const currentActiveModel = currentActiveModelRef.current;
				const currentModel3DLoaded = currentModel3DLoadedRef.current;

				console.log("Rendering decision:", {
					currentActiveModel,
					currentModel3DLoaded,
					glassesLoaded,
					glasses3DRef: !!glasses3DRef.current,
				});

				if (currentActiveModel === "2D" && glassesLoaded) {
					console.log("Rendering 2D glasses - 3D model disabled");

					// Ensure 3D model is hidden when 2D is active
					if (glasses3DRef.current) {
						glasses3DRef.current.visible = false;
					}

					// Use key landmarks for glasses positioning
					const leftEye = landmarks[33]; // Left eye outer corner
					const rightEye = landmarks[263]; // Right eye outer corner
					const noseTip = landmarks[1]; // Nose tip
					const leftFace = landmarks[127]; // Left face edge
					const rightFace = landmarks[356]; // Right face edge
					const forehead = landmarks[10]; // Forehead point

					// Convert to canvas coordinates WITHOUT mirroring
					const toCanvasCoords = landmark => {
						return {
							x: landmark.x * canvas.width,
							y: landmark.y * canvas.height,
						};
					};

					const leftEyeCanvas = toCanvasCoords(leftEye);
					const rightEyeCanvas = toCanvasCoords(rightEye);
					const noseTipCanvas = toCanvasCoords(noseTip);
					const leftFaceCanvas = toCanvasCoords(leftFace);
					const rightFaceCanvas = toCanvasCoords(rightFace);

					// Calculate face measurements
					const faceWidth = Math.abs(
						rightFaceCanvas.x - leftFaceCanvas.x
					);
					const eyeDistance = Math.sqrt(
						Math.pow(rightEyeCanvas.x - leftEyeCanvas.x, 2) +
							Math.pow(rightEyeCanvas.y - leftEyeCanvas.y, 2)
					);

					// Calculate glasses size based on face width and eye distance
					const baseWidth = faceWidth * 1;
					const minWidth = eyeDistance * 0.5;
					const maxWidth = eyeDistance * 1.1;

					let glassesWidth = Math.max(
						minWidth,
						Math.min(maxWidth, baseWidth)
					);

					// Adjust for distance from camera
					const distanceFactor = Math.min(
						1.5,
						Math.max(0.7, eyeDistance / 50)
					);
					glassesWidth = glassesWidth * distanceFactor;

					const glassesHeight =
						glassesWidth * (glasses.height / glasses.width);

					// Calculate center position between eyes
					const centerX = (leftEyeCanvas.x + rightEyeCanvas.x) / 2;
					const centerY = (leftEyeCanvas.y + rightEyeCanvas.y) / 2;

					// Calculate rotation angle
					const dx = rightEyeCanvas.x - leftEyeCanvas.x;
					const dy = rightEyeCanvas.y - leftEyeCanvas.y;
					const angle = Math.atan2(dy, dx);

					// Draw glasses
					ctx.save();

					// Apply transformations - NO mirroring
					ctx.translate(centerX, centerY);
					ctx.rotate(angle);

					// Add some transparency for blending
					ctx.globalAlpha = 0.9;

					// Draw the glasses image
					ctx.drawImage(
						glasses,
						-glassesWidth / 2,
						-glassesHeight / 2 - glassesHeight * 0.05, // Slight vertical adjustment
						glassesWidth,
						glassesHeight
					);

					ctx.restore();
				} else if (currentActiveModel === "3D") {
					console.log(
						"3D mode active - 2D model completely disabled",
						{
							model3DLoaded: currentModel3DLoaded,
							glasses3DRef: !!glasses3DRef.current,
						}
					);

					// FIXED: Ensure 2D model is completely disabled
					// Clear 2D canvas when 3D is active to prevent 2D model showing
					ctx.clearRect(0, 0, canvas.width, canvas.height);
					ctx.drawImage(
						results.image,
						0,
						0,
						canvas.width,
						canvas.height
					);

					// Render 3D glasses if 3D model is loaded
					if (currentModel3DLoaded && glasses3DRef.current) {
						console.log("Rendering 3D glasses with face detection");
						position3DGlassesOnFace(landmarks);
						render3DOverlay();
					} else {
						console.log("3D model not ready:", {
							model3DLoaded: currentModel3DLoaded,
							glasses3DRef: !!glasses3DRef.current,
							sceneRef: !!sceneRef.current,
						});
						// Show loading message on canvas
						ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
						ctx.fillRect(10, 10, 300, 40);
						ctx.fillStyle = "white";
						ctx.font = "14px Arial";
						ctx.fillText("Loading 3D model...", 20, 30);
					}

					// DO NOT render any 2D glasses in 3D mode - exit early
					return;
				}

				// DEBUG: Uncomment these lines to see landmarks and positioning
				/*
				// Draw eye points
				ctx.fillStyle = 'red';
				ctx.fillRect(leftEyeCanvas.x - 3, leftEyeCanvas.y - 3, 6, 6);
				ctx.fillRect(rightEyeCanvas.x - 3, rightEyeCanvas.y - 3, 6, 6);
				
				// Draw center point
				ctx.fillStyle = 'blue';
				ctx.fillRect(centerX - 3, centerY - 3, 6, 6);
				
				// Draw angle line
				ctx.strokeStyle = 'green';
				ctx.beginPath();
				ctx.moveTo(centerX - 50 * Math.cos(angle), centerY - 50 * Math.sin(angle));
				ctx.lineTo(centerX + 50 * Math.cos(angle), centerY + 50 * Math.sin(angle));
				ctx.stroke();

				// Display coordinates for debugging
				ctx.fillStyle = 'white';
				ctx.font = '12px Arial';
				ctx.fillText(`Left Eye: ${Math.round(leftEyeCanvas.x)}, ${Math.round(leftEyeCanvas.y)}`, 10, 20);
				ctx.fillText(`Right Eye: ${Math.round(rightEyeCanvas.x)}, ${Math.round(rightEyeCanvas.y)}`, 10, 40);
				ctx.fillText(`Center: ${Math.round(centerX)}, ${Math.round(centerY)}`, 10, 60);
				*/
			} else {
				setFaceDetected(false);
			}
		};

		// Setup FaceMesh with enhanced error handling and multiple CDN fallbacks
		const initializeFaceMesh = () => {
			try {
				faceMesh = new FaceMesh({
					locateFile: file => {
						// Multiple CDN sources for better reliability
						const cdnSources = [
							"https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh",
							"https://unpkg.com/@mediapipe/face_mesh",
							"https://cdn.skypack.dev/@mediapipe/face_mesh",
							"https://esm.sh/@mediapipe/face_mesh",
						];

						// Use the first CDN source
						const baseUrl = cdnSources[0];
						return `${baseUrl}/${file}`;
					},
					// Enhanced error handling for asset loading
					onError: error => {
						console.warn("MediaPipe FaceMesh error:", error);
						setMediaPipeError(true);

						// Try alternative CDN sources
						if (retryCount < maxRetries) {
							retryCount++;
							console.log(
								`Retrying MediaPipe initialization with alternative CDN (${retryCount}/${maxRetries})`
							);
							setTimeout(() => {
								initializeFaceMeshWithFallback();
							}, 3000);
						}
					},
				});

				faceMesh.setOptions({
					maxNumFaces: 1,
					refineLandmarks: true,
					minDetectionConfidence: 0.5,
					minTrackingConfidence: 0.5,
				});

				faceMesh.onResults(onResults);

				// Handle MediaPipe initialization errors
				faceMesh.onError = error => {
					console.error(
						"MediaPipe FaceMesh initialization error:",
						error
					);
				};
			} catch (error) {
				console.error(
					"Failed to initialize MediaPipe FaceMesh:",
					error
				);
				if (retryCount < maxRetries) {
					retryCount++;
					setTimeout(() => {
						initializeFaceMesh();
					}, 2000);
				}
			}
		};

		// Fallback initialization with alternative CDN sources
		const initializeFaceMeshWithFallback = () => {
			try {
				const cdnSources = [
					"https://unpkg.com/@mediapipe/face_mesh",
					"https://cdn.skypack.dev/@mediapipe/face_mesh",
					"https://esm.sh/@mediapipe/face_mesh",
					"https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh",
				];

				const selectedCDN = cdnSources[retryCount % cdnSources.length];
				console.log(`Trying CDN: ${selectedCDN}`);

				faceMesh = new FaceMesh({
					locateFile: file => {
						return `${selectedCDN}/${file}`;
					},
					onError: error => {
						console.warn(`CDN ${selectedCDN} failed:`, error);
						if (retryCount < maxRetries) {
							retryCount++;
							setTimeout(() => {
								initializeFaceMeshWithFallback();
							}, 2000);
						}
					},
				});

				faceMesh.setOptions({
					maxNumFaces: 1,
					refineLandmarks: true,
					minDetectionConfidence: 0.5,
					minTrackingConfidence: 0.5,
				});

				faceMesh.onResults(onResults);

				faceMesh.onError = error => {
					console.error(
						"MediaPipe FaceMesh initialization error:",
						error
					);
				};
			} catch (error) {
				console.error(
					"Failed to initialize MediaPipe FaceMesh with fallback:",
					error
				);
				if (retryCount < maxRetries) {
					retryCount++;
					setTimeout(() => {
						initializeFaceMeshWithFallback();
					}, 2000);
				}
			}
		};

		// Initialize FaceMesh with simplified approach
		initializeMediaPipeSimple();

		// Add a small delay to ensure MediaPipe is fully initialized
		setTimeout(() => {
			if (!faceMesh) {
				console.log("MediaPipe not initialized, trying fallback...");
				initializeMediaPipeSimple();
			}
		}, 1000);

		// Enhanced fallback mode with better error handling
		const fallbackMode = () => {
			console.log("MediaPipe failed, running in enhanced fallback mode");
			const canvas = canvasRef.current;
			const ctx = canvas.getContext("2d");

			const drawVideo = () => {
				if (videoRef.current && canvas) {
					ctx.clearRect(0, 0, canvas.width, canvas.height);
					ctx.drawImage(
						videoRef.current,
						0,
						0,
						canvas.width,
						canvas.height
					);

					// Enhanced fallback message
					ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
					ctx.fillRect(10, 10, 350, 60);
					ctx.fillStyle = "white";
					ctx.font = "14px Arial";
					ctx.fillText("Face detection unavailable", 20, 30);
					ctx.fillText(
						"Video mode only - MediaPipe CDN issues",
						20,
						50
					);
					ctx.fillText("Try refreshing the page", 20, 70);
				}
				requestAnimationFrame(drawVideo);
			};

			// Start fallback video display
			setTimeout(drawVideo, 1000);
		};

		// Alternative MediaPipe initialization with local fallback
		const initializeMediaPipeWithLocalFallback = () => {
			try {
				// Try to use a more reliable MediaPipe configuration
				faceMesh = new FaceMesh({
					locateFile: file => {
						// Try local fallback first, then CDN
						if (file.includes("face_mesh_solution_packed_assets")) {
							// Use a more reliable CDN for the main assets
							return `https://unpkg.com/@mediapipe/face_mesh/${file}`;
						}
						// For other files, use the primary CDN
						return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
					},
					onError: error => {
						console.warn("MediaPipe local fallback error:", error);
						setMediaPipeError(true);
					},
				});

				faceMesh.setOptions({
					maxNumFaces: 1,
					refineLandmarks: true,
					minDetectionConfidence: 0.5,
					minTrackingConfidence: 0.5,
				});

				faceMesh.onResults(onResults);

				faceMesh.onError = error => {
					console.error(
						"MediaPipe local fallback initialization error:",
						error
					);
				};
			} catch (error) {
				console.error(
					"Failed to initialize MediaPipe with local fallback:",
					error
				);
				fallbackMode();
			}
		};

		// Set up enhanced fallback if MediaPipe fails
		setTimeout(() => {
			if (!faceMesh) {
				console.log(
					"Primary MediaPipe failed, trying enhanced fallback..."
				);
				initializeMediaPipeWithLocalFallback();
			}
		}, 3000);

		// Final fallback if all MediaPipe attempts fail
		setTimeout(() => {
			if (!faceMesh) {
				console.log(
					"All MediaPipe attempts failed, using video-only mode"
				);
				fallbackMode();
			}
		}, 8000);

		// Setup camera with enhanced error handling
		if (videoRef.current) {
			camera = new Camera(videoRef.current, {
				onFrame: async () => {
					try {
						if (faceMesh && typeof faceMesh.send === "function") {
							await faceMesh.send({image: videoRef.current});
						}
					} catch (error) {
						console.error("FaceMesh error:", error);
						// Continue without face detection if MediaPipe fails
					}
				},
				width: 640,
				height: 480,
			});

			camera
				.start()
				.then(() => {
					console.log("Camera started successfully");
					setLoading(false);
				})
				.catch(error => {
					console.error("Camera start error:", error);
					setLoading(false);
					// Try fallback mode if camera fails
					fallbackMode();
				});
		}

		return () => {
			if (camera) {
				camera.stop();
			}
		};
	}, [glassesLoaded]);

	// Render AppCanvas with integrated 2D/3D functionality
	return (
		<div
			className="vto-modern-container"
			style={{position: "relative", width: 640, height: 480}}
		>
			{loading && (
				<div className="modern-loading-overlay">
					<div className="modern-spinner"></div>
					<p>Loading camera...</p>
				</div>
			)}

			{!glassesLoaded && activeModel === "2D" && (
				<div
					className="modern-loading-overlay"
					style={{background: "rgba(0,0,0,0.7)"}}
				>
					<div className="modern-spinner"></div>
					<p>Loading 2D glasses model...</p>
				</div>
			)}

			{!faceDetected &&
				activeModel === "2D" &&
				glassesLoaded &&
				!loading && (
					<div
						className="modern-loading-overlay"
						style={{
							background: "rgba(0,0,0,0.5)",
							color: "white",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<p>Position your face in the camera</p>
						<p style={{fontSize: "14px", marginTop: "10px"}}>
							Make sure your face is well lit and visible
						</p>
						{mediaPipeError && (
							<div
								style={{
									fontSize: "12px",
									marginTop: "10px",
									color: "#ffcc00",
									textAlign: "center",
								}}
							>
								<p>⚠️ Face detection may be limited</p>
								<p style={{fontSize: "10px", marginTop: "5px"}}>
									MediaPipe CDN issues detected. Try
									refreshing the page.
								</p>
							</div>
						)}
					</div>
				)}

			{/* Model Type Toggle - Only show if both models are available */}
			{modelType === "both" && (
				<div
					style={{
						position: "absolute",
						top: "10px",
						right: "10px",
						zIndex: 20,
						background: "rgba(0,0,0,0.7)",
						padding: "8px 12px",
						borderRadius: "6px",
						display: "flex",
						gap: "8px",
						alignItems: "center",
					}}
				>
					<button
						onClick={() => {
							console.log("Switching to 2D model");
							setActiveModel("2D");
						}}
						style={{
							background:
								activeModel === "2D"
									? "#007bff"
									: "transparent",
							color: "white",
							border: "1px solid #007bff",
							padding: "4px 8px",
							borderRadius: "4px",
							fontSize: "12px",
							cursor: "pointer",
						}}
					>
						2D
					</button>
					<button
						onClick={async () => {
							console.log("=== 3D BUTTON CLICKED ===", {
								currentActiveModel: activeModel,
								model3DLoaded,
								model3DUrl,
								modelType,
							});

							// FIXED: Use force activation for 3D mode
							forceActivate3DMode();

							// FIXED: Ensure 3D model is loaded when switching to 3D mode
							if (!model3DLoaded && model3DUrl) {
								console.log("Loading 3D model on switch...");
								try {
									await load3DModel(model3DUrl);
									console.log(
										"3D model loaded successfully on switch"
									);
								} catch (error) {
									console.error(
										"Failed to load 3D model on switch:",
										error
									);
								}
							} else if (model3DLoaded) {
								console.log(
									"3D model already loaded, activating..."
								);
							} else {
								console.log("No 3D model URL available");
							}

							console.log("=== 3D BUTTON CLICK COMPLETE ===");
						}}
						style={{
							background:
								activeModel === "3D"
									? "#007bff"
									: "transparent",
							color: "white",
							border: "1px solid #007bff",
							padding: "4px 8px",
							borderRadius: "4px",
							fontSize: "12px",
							cursor: "pointer",
						}}
					>
						3D
					</button>
				</div>
			)}

			{/* Model Type Indicator */}
			<div
				style={{
					position: "absolute",
					top: "10px",
					left: "10px",
					zIndex: 20,
					background: "rgba(0,0,0,0.7)",
					padding: "4px 8px",
					borderRadius: "4px",
					color: "white",
					fontSize: "12px",
					fontWeight: "600",
				}}
			>
				{activeModel === "2D" ? "2D Model" : "3D Model"}
				{activeModel === "2D" && (
					<div
						style={{
							fontSize: "10px",
							color: glassesLoaded ? "#00ff00" : "#ff0000",
						}}
					>
						{glassesLoaded ? "✓ Loaded" : "✗ Loading..."}
					</div>
				)}
			</div>

			<video
				ref={videoRef}
				style={{display: "none"}}
				width={640}
				height={480}
				autoPlay
				playsInline
				muted
			/>

			<canvas
				ref={canvasRef}
				width={640}
				height={480}
				className={
					loading || (activeModel === "2D" && !glassesLoaded)
						? "opacity-0"
						: "opacity-100 transition-opacity duration-700"
				}
				style={{
					width: "100%",
					height: "100%",
					transform: "scaleX(-1)", // CSS mirroring for natural selfie view
				}}
			/>

			{/* 3D Overlay Canvas - Only render when 3D model is active */}
			{activeModel === "3D" && (
				<canvas
					ref={overlayCanvasRef}
					width={640}
					height={480}
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						transform: "scaleX(-1)", // CSS mirroring for natural selfie view
						pointerEvents: "none",
					}}
				/>
			)}
		</div>
	);
});

export default AppCanvas;
